// mui
import { Button } from '@mui/material';

// components
import { Icon } from '@/components/atoms/Icon/Icon';
import AdminTablePage from '@/pwa/templates/AdminTablePage/AdminTablePage';
import DataTable, {
  GridColDef,
  GridRenderCellParams,
} from '@/pwa/organisms/DataTable/DataTable';
import {
  EvaluationChip,
  evaluationChipLabel,
} from '@/pwa/atoms/EvaluationChip/EvaluationChip';

// models
import {
  Backtest,
  BacktestRunSummary,
  BacktestTranscriptChoice,
} from '@karehero/models';
import { formatDateTime } from '@karehero/helpers/datetime';

// data
import { EMPTY_VALUE, TRANSCRIPT_CHOICE_LABEL } from './data';

const ROWS_PER_PAGE = 25;

const columns: GridColDef[] = [
  { field: 'sourceLabel', headerName: 'Source', flex: 1.4, minWidth: 220 },
  {
    field: 'transcript',
    headerName: 'Transcript',
    flex: 0.6,
    minWidth: 150,
    valueGetter: (value: BacktestTranscriptChoice) =>
      TRANSCRIPT_CHOICE_LABEL[value],
  },
  { field: 'runCount', headerName: 'Runs', flex: 0.3, minWidth: 80 },
  {
    field: 'latestRun',
    headerName: 'Latest run',
    flex: 0.6,
    minWidth: 150,
    valueGetter: (value?: BacktestRunSummary | null) =>
      value ? evaluationChipLabel('run', value.status) : EMPTY_VALUE,
    renderCell: ({ row }: GridRenderCellParams) =>
      row.latestRun ? (
        <EvaluationChip kind='run' value={row.latestRun.status} />
      ) : (
        EMPTY_VALUE
      ),
  },
  {
    field: 'createdAt',
    headerName: 'Created',
    flex: 0.7,
    minWidth: 160,
    renderCell: ({ value }: GridRenderCellParams) => formatDateTime(value),
  },
];

export interface AdminBacktestsPageProps {
  /** Most recently created first. */
  backtests: Backtest[];
  isLoading?: boolean;
  isError?: boolean;
  onNewBacktest: () => void;
  onOpenBacktest: (backtestID: string) => void;
  /** Shows a button to the sources when given. */
  onOpenSources?: () => void;
}

export const AdminBacktestsPage = ({
  backtests,
  isLoading = false,
  isError = false,
  onNewBacktest,
  onOpenBacktest,
  onOpenSources,
}: AdminBacktestsPageProps) => (
  <AdminTablePage
    title='Backtests'
    description='Run the AI fact extraction over a source and compare it with the answers the source expects.'
  >
    <DataTable
      columns={columns}
      rows={backtests}
      isLoading={isLoading}
      isError={isError}
      rowsPerPage={ROWS_PER_PAGE}
      noDataMsg='No backtests yet'
      defaultAction={{
        label: 'Open',
        icon: 'chevron-right',
        onPress: (backtest: Backtest) => onOpenBacktest(backtest.id),
      }}
      toolbarActions={
        <>
          {onOpenSources && (
            <Button variant='outlined' size='small' onClick={onOpenSources}>
              Sources
            </Button>
          )}
          <Button
            variant='contained'
            size='small'
            onClick={onNewBacktest}
            startIcon={<Icon icon='plus' size='sm' />}
          >
            New backtest
          </Button>
        </>
      }
    />
  </AdminTablePage>
);

AdminBacktestsPage.displayName = 'AdminBacktestsPage';
export default AdminBacktestsPage;
